import React from 'react';
import PropTypes from 'prop-types';

import {Autocomplete, TextField} from '@mui/material';

import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

const RelationshipAutocomplete = (props) => {
  const {
    autocompleteSx,
    getOptionDetail,
    label = '',
    options = [],
    selectedRelationship,
    setSelectedRelationship,
    textFieldSx,
    variant = 'outlined',
  } = props;

  const [internalInputValue, setInternalInputValue] = React.useState('');
  const selectedDisplayValue = selectedRelationship?.name || '';

  React.useEffect(() => {
    setInternalInputValue(selectedDisplayValue);
  }, [selectedDisplayValue]);

  const handleInputChange = (_event, newValue, reason) => {
    if (reason === 'input') {
      setInternalInputValue(newValue);
      return;
    }

    if (reason === 'clear') {
      setSelectedRelationship(null);
      setInternalInputValue('');
      return;
    }

    setInternalInputValue(selectedDisplayValue || newValue);
  };

  const handleOptionSelect = (_event, nextRelationship) => {
    setSelectedRelationship(nextRelationship);
    setInternalInputValue(nextRelationship?.name || '');
  };

  return (
    <Autocomplete
      options={options}
      sx={autocompleteSx}
      inputValue={internalInputValue}
      isOptionEqualToValue={(option, value) => option.relationshipRn === value.relationshipRn}
      value={selectedRelationship ?? null}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      getOptionLabel={(option) => option.name}
      filterOptions={(availableOptions, state) => {
        const inputToFilterBy = state.inputValue.toLowerCase();
        const selectedInputValue = selectedDisplayValue.toLowerCase();

        return availableOptions.filter((option) => {
          const optionName = (option.name).toLowerCase();
          const optionDetail = getOptionDetail(option);

          if (!optionDetail) {
            return false;
          }

          if (!inputToFilterBy || inputToFilterBy === selectedInputValue) {
            return true;
          }

          return optionName.includes(inputToFilterBy);
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          sx={{
            'minWidth': 'fit-content',
            '& .MuiInputBase-root': {
              height: 'inherit',
            },
            ...textFieldSx,
          }}
        />
      )}
      renderOption={(renderProps, option) => {
        const optionName = option.name;
        const optionDetail = getOptionDetail(option);
        const matches = match(optionName, internalInputValue);
        const parts = parse(optionName, matches);

        return (
          <li {...renderProps} key={option.relationshipRn}>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{fontWeight: part.highlight ? 700 : 400}}
                  >
                    {part.text.replace(/ /g, '\u00A0')}
                  </span>
                ))}
              </span>
              {
                optionDetail &&
                <span style={{fontSize: 12, color: '#6F6A62'}}>
                  {optionDetail}
                </span>
              }
            </div>
          </li>
        );
      }}
    />
  );
};

RelationshipAutocomplete.propTypes = {
  autocompleteSx: PropTypes.object,
  getOptionDetail: PropTypes.func.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  options: PropTypes.arrayOf(PropTypes.object),
  selectedRelationship: PropTypes.object,
  setSelectedRelationship: PropTypes.func.isRequired,
  textFieldSx: PropTypes.object,
  variant: PropTypes.string,
};

export default React.memo(RelationshipAutocomplete);
